import { loadHistoryServicesData } from '@pages/History/components/ShortLinkItem/components/ServiceHistoryItem/helpers';
import { ADDITIONAL_SERVICE_TYPE_CALCULATION, ROUTE_TYPE_CALCULATION } from '@pages/History/consts';
import { HistoryItem } from '@services/AuthService/query/loadUserQuery';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';

import { CALCULATIONS_MENU_ITEMS_LIMIT, DELIVERY_SECTION_ID } from '../CalculationsMenu/consts';
import { HISTORY_MENU_SERVICES_BATCH_SIZE } from './consts';
import { getServicesSectionId } from './helpers';

/**
 * Последние расчеты раздела для меню истории.
 * Раздел доп. услуг известен только из короткой ссылки, поэтому их грузим пачками и только при открытом меню.
 */
export const useHistoryMenuSections = (
  history: HistoryItem[],
  activeSectionId: string,
  isOpen: boolean
) => {
  const [sectionByItemId, setSectionByItemId] = useState<Record<string, string | null>>({});

  const sortedHistory = useMemo(
    () =>
      [...history].sort((a, b) => dayjs(b.created_at).valueOf() - dayjs(a.created_at).valueOf()),
    [history]
  );

  const items = useMemo(
    () =>
      sortedHistory
        .filter((item) => {
          switch (item.type_calculation) {
            case ROUTE_TYPE_CALCULATION:
              return activeSectionId === DELIVERY_SECTION_ID;
            case ADDITIONAL_SERVICE_TYPE_CALCULATION:
              return sectionByItemId[item.id] === activeSectionId;
            default:
              return false;
          }
        })
        .slice(0, CALCULATIONS_MENU_ITEMS_LIMIT),
    [sortedHistory, sectionByItemId, activeSectionId]
  );

  const unresolvedServiceItems = useMemo(
    () =>
      sortedHistory.filter(
        (item) =>
          item.type_calculation === ADDITIONAL_SERVICE_TYPE_CALCULATION &&
          !(item.id in sectionByItemId)
      ),
    [sortedHistory, sectionByItemId]
  );

  const isNeedLoadServices =
    isOpen &&
    activeSectionId !== DELIVERY_SECTION_ID &&
    items.length < CALCULATIONS_MENU_ITEMS_LIMIT &&
    unresolvedServiceItems.length > 0;

  useEffect(() => {
    if (!isNeedLoadServices) {
      return;
    }

    let isActual = true;
    const batch = unresolvedServiceItems.slice(0, HISTORY_MENU_SERVICES_BATCH_SIZE);

    Promise.all(
      batch.map((item) =>
        loadHistoryServicesData(item.short_link)
          .then((services) => [item.id, getServicesSectionId(services)] as const)
          .catch(() => [item.id, null] as const)
      )
    ).then((entries) => {
      if (!isActual) {
        return;
      }

      setSectionByItemId((prevState) => ({ ...prevState, ...Object.fromEntries(entries) }));
    });

    return () => {
      isActual = false;
    };
  }, [isNeedLoadServices, unresolvedServiceItems]);

  return {
    items,
    isLoading: isNeedLoadServices,
  };
};
